// ⚠️ If you modify the styling of this component and you're using the SectionListings component in your marketplace (featured listings)
// please reflect those changes in the calculateCarouselHeight function in SectionListing.js to avoid layout issues
import React, { useState } from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { requireListingImage } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { createSlug } from '../../util/urlHelpers';
import { vehiclePath } from '../../util/vehicleLinks';
import { verificationState } from '../../util/listingVerification';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
} from '../../components';

import { getListingCardTranslations } from './ListingCard.helpers';

import css from './ListingCard.module.css';

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.listing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    listing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    aspectRatioClassName,
    lazyLoadImage,
  } = props;

  const firstImage = listing?.images?.[0] || null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const aspectRatioClass = aspectRatioClassName || css.aspectRatioWrapper;
  const ImageComponent = lazyLoadImage ? LazyImage : ResponsiveImage;

  return (
    <AspectRatioWrapper
      className={aspectRatioClass}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <ImageComponent
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
    </AspectRatioWrapper>
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.aspectRatioClassName custom className for AspectRatioWrapper component
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const [shareStatus, setShareStatus] = useState(null);
  const [manualShareText, setManualShareText] = useState(null);

  const {
    className,
    rootClassName,
    aspectRatioClassName,
    darkMode,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    lazyLoadImage = true,
  } = props;

  const translations = getListingCardTranslations(listing, config, intl);
  const {
    titlePlain,
    titleFormatted,
    cardAriaLabel,
    showPrice,
    priceTooltip,
    priceMessage,
    authorName,
  } = translations;

  const classes = classNames(rootClassName || css.root, className);

  const id = listing?.id?.uuid;
  const { title = '', publicData } = listing?.attributes || {};
  // The same rule as the listing page: recorded, current checks only, never seller-entered data.
  const verified = verificationState(listing).verified;
  const slug = createSlug(title);

  const { listingType, cardStyle } = publicData || {};
  const validListingTypes = config.listing.listingTypes || [];
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  // Render the listing image only if listing images are enabled in the listing type
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(listing?.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  // Vehicle-specific fields from publicData for badges and specs strip
  const { odometer, year, fuelType, vehicleCategory, condition, province } = publicData || {};

  const isNew = condition === 'new' || vehicleCategory === 'new';
  const conditionLabel = isNew ? 'New' : 'Used';
  const conditionClass = isNew ? css.conditionNew : css.conditionUsed;

  const odometerLabel = odometer ? `${Number(odometer).toLocaleString('en-CA')} km` : null;
  const fuelLabel = fuelType ? fuelType.charAt(0).toUpperCase() + fuelType.slice(1) : null;
  const yearLabel = year ? String(year) : null;

  // The canonical public listing URL, not a homepage, referral, or draft link.
  const path = vehiclePath(listing);
  const rootURL =
    typeof config.marketplaceRootURL === 'string'
      ? config.marketplaceRootURL.replace(/\/$/, '')
      : '';
  const validRoot =
    /^https:\/\/[^/]+/.test(rootURL) || /^http:\/\/localhost(?::\d+)?$/.test(rootURL);
  const shareUrl = path && validRoot ? rootURL + path : null;
  const mileageRaw = [publicData?.odometer, publicData?.mileage].find(
    value =>
      value !== undefined &&
      value !== null &&
      value !== '' &&
      Number.isFinite(Number(value)) &&
      Number(value) >= 0
  );
  const mileage =
    mileageRaw === undefined
      ? null
      : Number(mileageRaw).toLocaleString('en-CA') +
        ' ' +
        (publicData?.mileageUnit === 'mi' ? 'mi' : 'km');
  const shareSummary = [
    title,
    showPrice && priceTooltip ? 'Price: ' + priceTooltip : null,
    mileage ? 'Mileage: ' + mileage : null,
    province ? 'Location: ' + province : null,
    publicData?.stockNo ? 'Stock #: ' + publicData.stockNo : null,
  ]
    .filter(Boolean)
    .join('\n');

  const handleShare = async () => {
    if (!shareUrl) return;
    setShareStatus(null);
    setManualShareText(null);
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({ title, text: shareSummary, url: shareUrl });
        return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }
    const text = shareSummary + '\n' + shareUrl;
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        setShareStatus('copied');
        return;
      } catch (error) {
        // Clipboard is blocked: let the customer select and copy instead.
      }
    }
    setManualShareText(text);
  };

  const cardLink = (
    <NamedLink
      className={shareUrl ? css.cardLink : classes}
      name="ListingPage"
      params={{ id, slug }}
      ariaLabel={cardAriaLabel}
    >
      {/* Image section with overlay badges */}
      <div className={css.imageWrapper}>
        {showListingImage ? (
          <ListingCardImage
            renderSizes={renderSizes}
            title={titlePlain}
            listing={listing}
            setActivePropsMaybe={setActivePropsMaybe}
            aspectWidth={aspectWidth}
            aspectHeight={aspectHeight}
            variantPrefix={variantPrefix}
            aspectRatioClassName={aspectRatioClassName}
            lazyLoadImage={lazyLoadImage}
          />
        ) : (
          <ListingCardThumbnail
            style={cardStyle}
            listingTitle={title}
            className={aspectRatioClassName}
            width={aspectWidth}
            height={aspectHeight}
            setActivePropsMaybe={setActivePropsMaybe}
          />
        )}
        <div className={css.imageOverlay} />
        <span className={classNames(css.conditionBadge, conditionClass)}>{conditionLabel}</span>
        {verified ? <span className={css.verifiedBadge}>✓ Verified</span> : null}
        {province && <span className={css.provinceBadge}>📍 {province}</span>}
      </div>

      <div className={css.info}>
        {showPrice ? (
          <div className={css.price} title={priceTooltip}>
            {priceMessage}
          </div>
        ) : null}
        <div className={css.priceDivider} />
        <div className={css.mainInfo}>
          {showListingImage && (
            <div className={classNames(css.title, { [css.lightText]: darkMode })}>
              {titleFormatted}
            </div>
          )}
          {showAuthorInfo ? (
            <div className={classNames(css.authorInfo, { [css.lightText]: darkMode })}>
              {authorName}
            </div>
          ) : null}
        </div>
        {(yearLabel || odometerLabel || fuelLabel) && (
          <div className={css.specsRow}>
            {yearLabel && <span className={css.specItem}>📅 {yearLabel}</span>}
            {odometerLabel && <span className={css.specItem}>🛣 {odometerLabel}</span>}
            {fuelLabel && <span className={css.specItem}>⛽ {fuelLabel}</span>}
          </div>
        )}
      </div>
    </NamedLink>
  );

  // Existing non-canonical/draft-like fixtures retain their original markup.
  // On real public listings, share is a sibling of the link (no nested button).
  if (!shareUrl) return cardLink;

  return (
    <div className={classes}>
      {cardLink}
      <div className={css.cardActions}>
        <button
          type="button"
          className={css.shareButton}
          onClick={handleShare}
          aria-label={
            intl.formatMessage({ id: 'ListingActions.share', defaultMessage: 'Share' }) +
            ' ' +
            title
          }
        >
          <span aria-hidden="true">↗</span>{' '}
          <FormattedMessage id="ListingActions.share" defaultMessage="Share" />
        </button>
        {shareStatus === 'copied' ? (
          <span className={css.shareFeedback} role="status">
            <FormattedMessage id="ListingActions.linkCopied" defaultMessage="Copied to clipboard" />
          </span>
        ) : null}
      </div>
      {manualShareText ? (
        <textarea
          className={css.shareFallback}
          aria-label="Select and copy vehicle details and link"
          readOnly
          value={manualShareText}
          onFocus={event => event.target.select()}
          onClick={event => event.target.select()}
        />
      ) : null}
    </div>
  );
};

export default ListingCard;
